import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewEncapsulation, Input } from '@angular/core';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { EmailItem } from 'common/workitems/interfaces/email-item.interface';
import { EmailList } from 'common/workitems/lists/email-list';
import { ComposeEmailList } from 'common/workitems/lists/compose-email-list';
import { WorkItemComposeEmail } from 'common/workitems/models/workitem-compose-email-model';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { workItemListEventRoutes } from 'common/workitems/lists/work-item-list-event-routes';
import { Subscription } from 'rxjs';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { WorkItemEvents } from 'common/common/constants/analytics-enums';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { EmailsService } from 'common/emails/emails.service';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { AudioService } from 'common/audio-service/audio.service';
import { taskItemAnimation } from 'components/accordion/workitem-list/shared/animations/task-item.animation';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { BcConfigService } from 'common/config/provider';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { WorkItem } from 'common/workitems/interfaces/workitem-base.interface';

@Component({
  selector: 'app-work-item-email-list',
  templateUrl: './workitem-email-list.component.html',
  styleUrls: ['./workitem-email-list.component.scss'],
  animations: [taskItemAnimation],
  encapsulation: ViewEncapsulation.None
})
export class WorkItemEmailListComponent implements OnInit, OnDestroy {
  @Input()
  isHybrid: boolean;
  public emailWorkItemList: EmailItem[];
  public initialInsertIsDone;
  public isSkeletonLoadingOn: boolean = false;

  private _emailListInsertSubscription: Subscription;
  private _emailListRemoveSubscription: Subscription;
  private _emailListUpdateSubscription: Subscription;
  private _composeEmailRemovedSubscription: Subscription;

  constructor(private _workItemListService: WorkItemListService,
              private _workItemListenerService: WorkItemListenerService,
              private _xlcEmails: EmailsService,
              private _workAreaManager: WorkAreaManager,
              private _audioService: AudioService,
              private _emailList: EmailList,
              private _composeEmailList: ComposeEmailList,
              private _rxEmitter: RxEmitter,
              private _workItemStateHandler: WorkItemStateHandler,
              private _bcAnalytics: BcAnalytics,
              private _changeDetector: ChangeDetectorRef,
              private _config: BcConfigService) {

    this.emailWorkItemList = this._emailList.emailWorkItemList;
    this._workItemListService.emailWorkItemList = this.emailWorkItemList;

    this._workItemListService.emailComposeList = this._composeEmailList.emailComposeWorkItems;

    this.initialInsertIsDone = false;
    this._emailListInsertSubscription = null;
    this._emailListRemoveSubscription = null;
    this._emailListUpdateSubscription = null;
  }

  ngOnInit() {
    this.isSkeletonLoadingOn = this._config.current.features.skeletonLoading;
    this.createStoreSubscriptionForEmails();
    this._composeEmailList.loadEmailComposeItems();

    this._composeEmailRemovedSubscription = this._rxEmitter.subscribe(workItemListEventRoutes.composeEmailRemoved, (workItem) => {
      this._composeEmailList.resetComposeEmail();
      this._workItemListService.removeWorkItem(workItem);
    });
  }

  ngOnDestroy() {
    this._emailListInsertSubscription.unsubscribe();
    this._emailListRemoveSubscription.unsubscribe();
    this._emailListUpdateSubscription.unsubscribe();
    this._composeEmailRemovedSubscription.unsubscribe();

    this._emailList.resetEmails();
    this._composeEmailList.updateEmailComposeStorage();
  }

  public getEmailStateIcon(isNewItem: boolean): string {
    return isNewItem ? 'mail' : 'mail-open';
  }

  public isEmailComposeStateActive(): boolean {
    const selectedWorkItem = this._workAreaManager.getSelectedWorkItem();
    return selectedWorkItem && selectedWorkItem.isComposeEmail();
  }

  public isEmailComposeOpen(): boolean {
    const emailComposeWorkItem = this._composeEmailList.actualEmailComposeWorkItem;
    return emailComposeWorkItem && this._workItemStateHandler.isOpened(emailComposeWorkItem.id);
  }

  public createStoreSubscriptionForEmails(): void {
    this._workItemListenerService.createPromiseForInitialLoadingDone()
      .then(() => this.notifyInitialInsertIsDone());

    this._emailListInsertSubscription = this._workItemListenerService.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.EMAIL)
      .subscribe((emailItem: WorkItem & EmailItem) => this._insertEmailWorkItem(emailItem));

    this._emailListRemoveSubscription = this._workItemListenerService.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.EMAIL)
      .subscribe((emailItem: WorkItem & EmailItem) => this._removeEmailWorkItem(emailItem));

    this._emailListUpdateSubscription = this._workItemListenerService.createAcdBasedObservableForUpdateByWorkItemType(WorkItemTypes.EMAIL)
      .subscribe((emailItem: WorkItem & EmailItem) => this._updateEmailWorkItem(emailItem));
  }

  public notifyInitialInsertIsDone(): void {
    this.initialInsertIsDone = true;
    this._sortEmailWorkItems();
  }

  public setInitialEmailWorkItem(workItem: EmailItem): void {
    const item = {
      NewItem: !workItem.Answered,
      UserDisplayName: workItem.Name,
      IsAnswered: this._workItemListService.isAnswered(workItem),
      unansweredMessages: 0,
      secondsRemainingDisplay: '',
      showSecondsRemaining: false,
      secondsRemainingTimerPromise: null
    };

    workItem.decorateWithDefaultValues(item);

    this._workItemListService.addTimerToItemIfApplicable(workItem);

    this.emailWorkItemList.push(workItem);

    this._sortEmailWorkItems();
  }

  public buildEmailWorkItem(workItem: EmailItem, isUpdate?: boolean): Promise<any> {
    return this._xlcEmails.getEmailThread(workItem.id)
      .then((details) => {
        const item = this.getEmailWorkitemById(workItem.id);
        item.FirstLine = details.firstLine;
        item.UserEmailName = details.userEmailName;
        item.UserDisplayName = details.userEmailName || item.Name;

        if (!isUpdate) {
          this.generateWorkitemDepartmentFolder(item);
          this._workItemListService.updateParentUnansweredTimeout();
        }
      });
  }

  public generateWorkitemDepartmentFolder(workitem: EmailItem): void {
    const departmentFolder = [];
    if (workitem) {
      if (workitem.FolderName) {
        departmentFolder.push(workitem.FolderName);
      }
      if (workitem.DepartmentName) {
        departmentFolder.push(workitem.DepartmentName);
      }
      workitem.departmentFolder = departmentFolder.join(', ');
    }
  }

  private _removeEmailWorkItem(workItem: EmailItem): void {
    const email = this.getEmailWorkitemById(workItem.id);
    if (email) {
      this._workItemListService.stopTimerForItem(email);
      this.emailWorkItemList.splice(this.emailWorkItemList.indexOf(email), 1);
    }

    this._workItemListService.removeWorkItem(workItem);
  }

  public selectWorkItem(workItem: EmailItem): void {
    this._workItemListService.selectAndAcceptEmailItem(workItem);
  }

  public onWorkItemKeydown(event, workitem): void {
    if (event.which === KeyboardKeyCodes.ENTER) {
      event.preventDefault();
      event.stopPropagation();
      this.selectWorkItem(workitem);
      this._changeDetector.detectChanges();
    }
  }

  public onComposeEmailKeydown(event): void {
    if (event.which === KeyboardKeyCodes.ENTER) {
      event.preventDefault();
      event.stopPropagation();
      this.selectWorkItem((<EmailItem>this._composeEmailList.actualEmailComposeWorkItem));
      this._changeDetector.detectChanges();
    }
  }

  public generateWorkitemClass(workItem: EmailItem): string {
    return this._workItemListService.generateWorkitemClass(workItem);
  }

  public getEmailWorkitemById(workItemId): EmailItem {
    return this.emailWorkItemList.find(workItem => workItem.id === workItemId);
  }

  public getEmailWorkitemCount(): number {
    return this._emailList.getEmailWorkItemCount();
  }

  public composeEmail(): void {
    const workItem: WorkItemComposeEmail = this._composeEmailList.createEmailComposeElement();
    this._bcAnalytics.eventTrack(WorkItemEvents.COMPOSE_EMAIL);

    if (workItem) {
      this._workItemListService.selectWorkitem(workItem);
    }
  }

  public selectComposeEmailItem(): void {
    this.selectWorkItem(this._composeEmailList.actualEmailComposeWorkItem);
    this._composeEmailList.updateEmailComposeStorage();
  }

  public canComposeMoreEmails(): boolean {
    return this._composeEmailList.canComposeMoreEmails;
  }

  public hasComposeEmailItem(): boolean {
    return !this._composeEmailList.canComposeMoreEmails;
  }

  public trackByWorkId(index, workItem): string {
    return workItem.id;
  }

  private _insertEmailWorkItem(workItem: EmailItem): void {
    this.setInitialEmailWorkItem(workItem);
    this.buildEmailWorkItem(workItem);
    if (this.initialInsertIsDone) {
      this._audioService.playNewEmailArrived();
    }
  }

  private _sortEmailWorkItems(): void {
    if (this.initialInsertIsDone) {
      this.emailWorkItemList.sort(this._emailList.emailComparator.bind(this._emailList));
    }
  }

  private async _updateEmailWorkItem(changes): Promise<void> {
    const workItem = this.getEmailWorkitemById(changes.id);
    this._workItemListService.updateWorkItem(workItem, changes);
    await this.buildEmailWorkItem(workItem, true);
    this.generateWorkitemDepartmentFolder(workItem);
    workItem.NewItem = !workItem.Answered;

    this._sortEmailWorkItems();
    this._rxEmitter.send(`${MessageBusEvents.EmailWorkItemUpdate}${workItem.id}`, null);
  }
}
